import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
} from "@angular/core";
import { AlertController } from "@ionic/angular";
import { TranslateModule } from "@ngx-translate/core";
import { Swiper, SwiperOptions } from "swiper";

// Services
import { TrackingService } from "@providers/TrackingService";
import { DeviceService } from "@services/device.service";
import { SportService } from "@sports-services/index";

import { SportsExtractDatePipe } from "@sports-pipes/index";
// Pipes
import { SportsFormatOddPipe } from "@sports-pipes/sports.format-odd.pipe";

// Stores
import { NewTicketFacade } from "@sports-features/ticket/stores";

// Models
import { BetbuilderItem, SmartLineItem } from "@ticket/models";

import { Store } from "@ngrx/store";
// Utils
import { Utils } from "@sports-utils/Utils";
import { Subject, takeUntil } from "rxjs";

// components
import { SbSportIconComponent } from "@sports-components/sb-sport-icon/sb-sport-icon.component";

@Component({
  selector: "sb-marquee-betbuilder",
  templateUrl: "sb-marquee-betbuilder.html",
  standalone: true,
  imports: [CommonModule, SportsExtractDatePipe, SportsFormatOddPipe, TranslateModule, SbSportIconComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styleUrls: ["./sb-marquee-betbuilder.scss"],
})
export class SbMarqueeBetbuilderComponent implements OnChanges, AfterViewInit, OnDestroy {
  @ViewChildren("list") list: QueryList<ElementRef>;
  @ViewChild("swiper") swiperContainer: ElementRef;
  @Input() betbuilderEventsData: BetbuilderItem[];

  public swiper;
  config: SwiperOptions = {
    loop: false,
    slidesPerView: "auto",
    observer: true,
    followFinger: true,
    grabCursor: true,
    allowSlidePrev: true,
    allowSlideNext: true,
    simulateTouch: true,
    preventClicks: true,
    preventClicksPropagation: true,
    spaceBetween: 0,
    direction: "horizontal",
    noSwiping: false,
    a11y: undefined,
  };

  betbuilderItems: BetbuilderItem[] = [];
  private ngUnsubscribe$ = new Subject<boolean>();
  smartLineItems: SmartLineItem[];
  isMobile = false;

  extractDate = inject(SportsExtractDatePipe);
  trackingService = inject(TrackingService);
  newTicketFacade = inject(NewTicketFacade);
  sportService = inject(SportService);
  alertCtrl = inject(AlertController);
  store = inject(Store<SmartLineItem[]>);
  public utils = inject(Utils);
  deviceService = inject(DeviceService);

  constructor() {
    this.isMobile = this.deviceService.isMobile();
  }

  ngOnChanges() {
    if (this.betbuilderEventsData.length > 0) {
      this.betbuilderItems = this.betbuilderEventsData.map((betbuilderEvent) => {
        const targetSmartLine = this.smartLineItems?.find(
          (item) => item.BetBuilderId === betbuilderEvent.BetBuilderId,
        );
        return {
          ...betbuilderEvent,
          isSelected: !!targetSmartLine,
          ItemId: targetSmartLine?.ItemId,
        };
      });
    }
    if (!this.swiper) return;
    this.swiper.update();
  }

  ngAfterViewInit() {
    this.swiper = new Swiper(this.swiperContainer.nativeElement, this.config);
    this.store
      .select((state) => state.ticket.ticket?.SmartLineItems)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((smartLineItems) => {
        this.smartLineItems = smartLineItems;
        if (this.betbuilderItems?.length) {
          this.betbuilderItems = this.betbuilderItems.map((betbuilderItem) => {
            const targetSmartLine = this.smartLineItems?.find(
              (item) => item.BetBuilderId === betbuilderItem.BetBuilderId,
            );
            return {
              ...betbuilderItem,
              isSelected: !!targetSmartLine,
              ItemId: targetSmartLine?.ItemId,
            };
          });
        }
      });
  }

  navigate(NodeId: string) {
    this.sportService
      .getGameByNodeId(NodeId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((data) => {
        if (!data) {
          this.showDismiss("", "No hay mercados abiertos en este evento");
          return;
        }
        const nodeId = data.NodeId;
        const parentNodeId = data.ParentNodeId;
        this.sportService.deepLinkFromMarquee(parentNodeId, nodeId);
      });
  }

  async showDismiss(title: string, msg: string) {
    const alertOptions = {
      enableBackdropDismiss: false,
      title: title,
      message: msg,
      buttons: [
        {
          role: "cancel",
          text: "OK",
          handler: () => {
            console.log("Alert dismissed");
          },
        },
      ],
    };
    const alert = await this.alertCtrl.create(alertOptions);
    alert.present();
  }

  bet(betbuilderItem) {
    this.newTicketFacade.betbuilderFromMarquee(betbuilderItem);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next(true);
    this.ngUnsubscribe$.complete();
  }

  trackBy(_index, item) {
    return item.BetBuilderId;
  }

  trackMarketInfoList(_index, item) {
    return item.ResultId;
  }
}
